import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../api/axiosInstance";
import Editor from "../../../components/RichTextEditor/Editor"

const API = "/api/superadmin/blog-categories-page-master/";

/* ------------------------------ defaults ------------------------------ */

// Auto-prefilled canonical — change to the real public URL of the blog categories page
const CANONICAL_DEFAULT = "https://procman.ai/blog";

const defaultSchema = {
  enable_webpage: true,
  custom_json: "",
  additional_schema_type: "",
  additional_schema_data: {},
};

const defaults = {
  hero: {
    heading: "Explore Industry Insights",
    subheading: "Browse all categories",
  },
  seo: {
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    canonical_url: CANONICAL_DEFAULT,
    og_title: "",
    og_description: "",
    twitter_title: "",
    twitter_description: "",
    robots_index: true,
    seo_content: ""
  },
  schema: defaultSchema,
  hero_background_image: null,
  og_image: null,
  twitter_image: null,
};

const IMAGE_SPECS = {
  hero_background_image: {
    formats: ["image/png", "image/jpeg", "image/webp"],
    maxMB: 3,
    minWidth: 1920,
    minHeight: 600,
    help: "PNG, JPG or WebP · max 3 MB · recommended 1920×600 px",
  },
  og_image: {
    formats: ["image/png", "image/jpeg", "image/webp"],
    maxMB: 2,
    help: "PNG, JPG or WebP · max 2 MB · recommended 1200×630",
  },
  twitter_image: {
    formats: ["image/png", "image/jpeg", "image/webp"],
    maxMB: 2,
    help: "PNG, JPG or WebP · max 2 MB · recommended 1200×630",
  },
};

const LIMITS = {
  "hero.heading": { max: 80, required: true, label: "Heading" },
  "hero.subheading": { max: 200, label: "Subheading" },
  "seo.meta_title": { max: 60, label: "Meta Title" },
  "seo.meta_description": { max: 160, label: "Meta Description" },
  "seo.meta_keywords": { max: 255, label: "Meta Keywords" },
  "seo.og_title": { max: 70, label: "OG Title" },
  "seo.og_description": { max: 200, label: "OG Description" },
  "seo.twitter_title": { max: 70, label: "Twitter Title" },
  "seo.twitter_description": { max: 200, label: "Twitter Description" },
};

const ADDITIONAL_SCHEMA_FIELDS = {
  Organization: [
    ["name", "Organization name"],
    ["url", "Organization URL"],
    ["description", "Organization description", true],
    ["logo", "Logo URL"],
  ],
  Service: [
    ["name", "Service name"],
    ["description", "Service description", true],
    ["url", "Service URL"],
    ["provider_name", "Provider name"],
  ],
  SoftwareApplication: [
    ["name", "Application name"],
    ["description", "Application description", true],
    ["url", "Application URL"],
    ["application_category", "Application category"],
    ["operating_system", "Operating system"],
  ],
};

const buildAdditionalSchema = (type, values = {}) => {
  const value = (key, fallback = "") => values[key]?.trim() || fallback;
  switch (type) {
    case "Organization":
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: value("name"),
        url: value("url"),
        description: value("description"),
        logo: value("logo"),
      };
    case "Service":
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        name: value("name"),
        description: value("description"),
        url: value("url"),
        provider: { "@type": "Organization", name: value("provider_name", "Procman") },
      };
    case "SoftwareApplication":
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: value("name"),
        description: value("description"),
        url: value("url"),
        applicationCategory: value("application_category", "BusinessApplication"),
        operatingSystem: value("operating_system", "Web"),
      };
    default:
      return null;
  }
};

/* ------------------------------- helpers ------------------------------ */


const withSeoFallback = (seo) => {
  const og_title = seo.og_title?.trim() || seo.meta_title || "";
  const og_description = seo.og_description?.trim() || seo.meta_description || "";
  return {
    ...seo,
    og_title,
    og_description,
    twitter_title: seo.twitter_title?.trim() || og_title,
    twitter_description: seo.twitter_description?.trim() || og_description,
  };
};

const merge = (data = {}) => ({
  ...defaults,
  hero: { ...defaults.hero, ...(data.hero || {}) },
  // seo: {
  //   ...defaults.seo,
  //   ...(data.seo || {}),
  //   // auto-prefill canonical when backend value is empty
  //   canonical_url: data.seo?.canonical_url || CANONICAL_DEFAULT,
  //   // twitter title/description fall back to OG values by default
  //   // twitter_title: data.seo?.twitter_title || data.seo?.og_title || "",
  //   // twitter_description: data.seo?.twitter_description || data.seo?.og_description || "",
  // },
    seo: withSeoFallback({
    ...defaults.seo,
    ...(data.seo || {}),
    canonical_url: data.seo?.canonical_url || CANONICAL_DEFAULT,
  }),
  schema: {
    ...defaultSchema,
    ...(data.schema && typeof data.schema === "object" ? data.schema : {}),
    custom_json:
      typeof data.schema?.custom_json === "string"
        ? data.schema.custom_json
        : data.schema?.custom_json
          ? JSON.stringify(data.schema.custom_json, null, 2)
          : "",
    additional_schema_data:
      data.schema?.additional_schema_data && typeof data.schema.additional_schema_data === "object"
        ? data.schema.additional_schema_data
        : {},
  },
  hero_background_image: data.hero_background_image ?? null,
  og_image: data.og_image ?? null,
  twitter_image: data.twitter_image ?? null,
});


const resolveSeo = (seo) => {
  const og_title = seo.og_title?.trim() || seo.meta_title || "";
  const og_description = seo.og_description?.trim() || seo.meta_description || "";
  return {
    ...seo,
    og_title,
    og_description,
    twitter_title: seo.twitter_title?.trim() || og_title,
    twitter_description: seo.twitter_description?.trim() || og_description,
  };
};
const getByPath = (obj, path) => path.split(".").reduce((o, k) => o?.[k], obj);

const validateField = (path, value) => {
  const rule = LIMITS[path];
  if (!rule) return null;
  const len = (value || "").toString().trim().length;
  if (rule.required && !len) return `${rule.label} is required.`;
  if (rule.max && len > rule.max) return `${rule.label} must be ${rule.max} characters or fewer.`;
  return null;
};

const validateSchemaText = (text) => {
  if (!text.trim()) return null;
  try {
    const parsed = JSON.parse(text);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return "Schema must be a JSON object.";
    }
    return null;
  } catch (e) {
    return `Invalid JSON: ${e.message}`;
  }
};

const readImageSize = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ w: img.naturalWidth, h: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });

const validateImage = async (file, fieldName) => {
  const spec = IMAGE_SPECS[fieldName];
  const problems = [];
  if (!spec) return problems;
  if (!spec.formats.includes(file.type)) problems.push("Only PNG, JPG or WebP files are allowed.");
  if (file.size > spec.maxMB * 1024 * 1024) problems.push(`File must be ${spec.maxMB} MB or smaller.`);
  if (!problems.length) {
    const size = await readImageSize(file);
    if (!size) {
      problems.push("Could not read this image file.");
    } else {
      if (spec.minWidth && size.w < spec.minWidth)
        problems.push(`Image width must be at least ${spec.minWidth}px (yours is ${size.w}px).`);
      if (spec.minHeight && size.h < spec.minHeight)
        problems.push(`Image height must be at least ${spec.minHeight}px (yours is ${size.h}px).`);
    }
  }
  return problems;
};

/* ------------------------------ UI pieces ----------------------------- */

const inputCls = (invalid) =>
  // `mt-1.5 w-full rounded-lg border-[1.5px] px-3.5 py-2.5 text-sm text-slate-900 transition outline-none
  `mt-1.5 w-full rounded-lg border-[1.5px] px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 transition outline-none
   focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15
   ${invalid ? "border-rose-600 bg-rose-50" : "border-slate-300 bg-white"}`;

const Section = ({ title, description, children }) => (
  <section className="mb-7 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:px-7">
    <div className="mb-5 border-b-2 border-slate-100 pb-3">
      <h2 className="text-xl font-bold tracking-tight text-slate-900">{title}</h2>
      {description && <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">{description}</p>}
    </div>
    <div className="grid gap-3">{children}</div>
  </section>
);

const SubBlock = ({ title, description, children }) => (
  <div>
    {title && (
      <div className="mb-3 inline-block rounded-md border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-slate-700">
        {title}
      </div>
    )}
    {description && <p className="mb-3.5 text-[13px] leading-normal text-slate-500">{description}</p>}
    {children}
  </div>
);

const Field = ({ label, value, onChange, textarea, rows = 3, rule, error, help, placeholder }) => {
  const len = (value || "").toString().trim().length;
  const invalid = Boolean(error);
  return (
    <label className="mb-4 block text-[13px] font-semibold text-slate-800">
      <div className="flex items-baseline justify-between gap-2">
        <span>
          {label}
          {rule?.required && <span className="text-rose-600"> *</span>}
        </span>
        {rule?.max && (
          <span className={`whitespace-nowrap text-[11px] font-semibold ${invalid ? "text-rose-600" : "text-slate-500"}`}>
            {len}/{rule.max}
          </span>
        )}
      </div>
      {textarea ? (
        <textarea
          rows={rows}
          className={inputCls(invalid)}
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type="text"
          className={inputCls(invalid)}
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {help && !error && <div className="mt-1.5 text-xs font-normal text-slate-500">{help}</div>}
      {error && <div className="mt-1.5 text-xs font-semibold text-rose-600">{error}</div>}
    </label>
  );
};

function ImageUploadField({
  label,
  fieldName,
  value,
  defaultValue,
  defaultNote,
  disabled,
  uploading,
  onUpload,
  onPreview,
  error,
  setError,
}) {
  const [localPreview, setLocalPreview] = useState(null);
  const spec = IMAGE_SPECS[fieldName];
  const isCustom = Boolean(localPreview || value);
  const previewUrl = localPreview || value || defaultValue;
  const blocked = disabled || uploading;

  const handleFile = async (file) => {
    if (!file) return;
    const problems = await validateImage(file, fieldName);
    if (problems.length) {
      setError(problems.join(" "));
      return;
    }
    setError(null);
    setLocalPreview(URL.createObjectURL(file));
    onUpload(fieldName, file);
  };

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
      <div className="mb-2 text-[13px] font-bold text-slate-800">{label}</div>
      <div className="grid grid-cols-[130px_1fr] items-center gap-4">
        <div
          className={`grid h-20 w-[130px] place-items-center overflow-hidden rounded-lg border-[1.5px] border-dashed bg-white ${error ? "border-red-300" : "border-slate-300"
            }`}
        >
          {previewUrl ? (
            <button
              type="button"
              aria-label={`Preview ${label}`}
              onClick={() => onPreview(previewUrl)}
              className="h-full w-full cursor-zoom-in"
            >
              <img src={previewUrl} alt={label} className="h-full w-full object-cover" />
            </button>
          ) : (
            <span className="p-1.5 text-center text-[11px] font-medium text-slate-500">No image</span>
          )}
        </div>
        <div>
          <input
            id={`img-${fieldName}`}
            type="file"
            className="hidden"
            accept={spec.formats.join(",")}
            disabled={blocked}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              handleFile(file);
            }}
          />
          <label
            htmlFor={`img-${fieldName}`}
            className={`inline-block rounded-md border border-blue-200 bg-blue-50 px-3.5 py-2 text-[13px] font-bold text-blue-600 transition ${blocked ? "cursor-not-allowed opacity-55" : "cursor-pointer hover:bg-blue-100"
              }`}
          >
            {uploading ? "Uploading…" : isCustom ? "Replace image" : "Upload image"}
          </label>
          {spec?.help && !error && <p className="mt-2 text-xs leading-snug text-slate-500">{spec.help}</p>}
          {!isCustom && previewUrl && defaultNote && !error && (
            <p className="mt-2 text-xs leading-snug text-slate-500">{defaultNote}</p>
          )}
          {disabled && (
            <p className="mt-2 text-xs leading-snug text-slate-500">Save the page once before uploading images.</p>
          )}
          {error && <p className="mt-2 text-xs font-semibold text-rose-600">{error}</p>}
        </div>
      </div>
    </div>
  );
}

function AdditionalSchemaGenerator({ schema, onChange }) {
  const [preview, setPreview] = useState(null);
  const selectedType = schema.additional_schema_type || "";
  const values = schema.additional_schema_data || {};
  const fields = ADDITIONAL_SCHEMA_FIELDS[selectedType] || [];
  const updateValue = (key, value) =>
    onChange({ ...schema, additional_schema_data: { ...values, [key]: value } });

  return (
    <SubBlock title="Generate an additional schema">
      <label className="mb-3 block text-[13px] font-semibold text-slate-800">
        Schema type
        <select
          className={inputCls(false)}
          value={selectedType}
          onChange={(e) => {
            onChange({ ...schema, additional_schema_type: e.target.value, additional_schema_data: {} });
            setPreview(null);
          }}
        >
          <option value="">None</option>
          <option value="Organization">Organization</option>
          <option value="Service">Service</option>
          <option value="SoftwareApplication">Software Application</option>
        </select>
      </label>
      {fields.map(([key, label, textarea]) => (
        <Field key={key} label={label} value={values[key]} textarea={textarea} onChange={(v) => updateValue(key, v)} />
      ))}
      {selectedType && (
        <>
          <button
            type="button"
            onClick={() => setPreview(buildAdditionalSchema(selectedType, values))}
            className="mt-2 rounded-lg bg-blue-600 px-[18px] py-[9px] text-[13px] font-semibold text-white transition hover:bg-blue-700"
          >
            Generate schema
          </button>
          {preview && (
            <pre className="mt-3.5 overflow-x-auto rounded-lg bg-slate-900 p-3.5 text-xs leading-normal text-slate-200">
              {JSON.stringify(preview, null, 2)}
            </pre>
          )}
        </>
      )}
    </SubBlock>
  );
}

/* ------------------------------ main page ----------------------------- */

export default function BlogCategoriesPageMaster() {
  const [record, setRecord] = useState(null);
  const [content, setContent] = useState(defaults);
  const [notice, setNotice] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [overlayUrl, setOverlayUrl] = useState("");

  const applyRow = (row) => {
    setRecord(row);
    setContent(merge(row));
  };

  useEffect(() => {
    axiosInstance
      .get(API)
      .then(({ data }) => {
        // list returns a single object in `data` (not an array) — handle both just in case
        const row = Array.isArray(data?.data) ? data.data[0] : data?.data;
        if (row && row.id) applyRow(row);
      })
      .catch(() => setNotice("Could not load Blog Categories page."))
      .finally(() => setLoaded(true));
  }, []);

  // const setGroup = (group, key, value) =>
  //   setContent((s) => {
  //     const next = { ...s, [group]: { ...s[group], [key]: value } };
  //     if (group !== "seo") return next;

  //     const old = s.seo;
  //     // target tab tak source ko follow kare jab tak empty ho ya old source ke barabar ho
  //     const sync = (target, source) => {
  //       if (!old[target] || old[target] === old[source]) {
  //         next.seo[target] = next.seo[source];
  //       }
  //     };

  //     if (key === "meta_title") {
  //       sync("og_title", "meta_title");
  //       sync("twitter_title", "og_title");
  //     }
  //     if (key === "meta_description") {
  //       sync("og_description", "meta_description");
  //       sync("twitter_description", "og_description");
  //     }
  //     if (key === "og_title") sync("twitter_title", "og_title");
  //     if (key === "og_description") sync("twitter_description", "og_description");

  //     return next;
  //   });

const setGroup = (group, key, value) =>
  setContent((s) => {
    const nextGroup = { ...s[group], [key]: value };

    // Meta title/description change hone par OG + Twitter auto-sync
    if (group === "seo" && key === "meta_title") {
      nextGroup.og_title = value;
      nextGroup.twitter_title = value;
    }
    if (group === "seo" && key === "meta_description") {
      nextGroup.og_description = value;
      nextGroup.twitter_description = value;
    }
    // OG edit karne par Twitter bhi follow kare (pehle jaisa behaviour)
    if (group === "seo" && key === "og_title") nextGroup.twitter_title = value;
    if (group === "seo" && key === "og_description") nextGroup.twitter_description = value;

    return { ...s, [group]: nextGroup };
  });
  const setField = (group, value) => setContent((s) => ({ ...s, [group]: value }));

  /* validation (derived — no separate error state to keep in sync) */
  const errors = useMemo(() => {
    const out = {};
    Object.keys(LIMITS).forEach((path) => {
      const msg = validateField(path, getByPath(content, path));
      if (msg) out[path] = msg;
    });
    const schemaErr = validateSchemaText(content.schema.custom_json || "");
    if (schemaErr) out["schema.custom_json"] = schemaErr;
    return out;
  }, [content]);

  const errorCount = Object.keys(errors).length;
  const hasBlockingErrors = errorCount > 0;

  const refresh = async (id) => {
    const targetId = id || record?.id;
    if (!targetId) return;
    const { data } = await axiosInstance.get(API);
    const row = Array.isArray(data?.data) ? data.data[0] : data?.data;
    if (row) applyRow(row);
  };

  const save = async () => {
    if (hasBlockingErrors) {
      setNotice(`Fix ${errorCount} validation error(s) before saving.`);
      return;
    }
    const payload = {
      hero: content.hero,
      seo: resolveSeo(content.seo),
      schema: content.schema,
    };
    setSaving(true);
    try {
      if (record) {
        await axiosInstance.put(`${API}${record.id}/`, payload);
        await refresh(record.id);
      } else {
        const res = await axiosInstance.post(API, payload);
        await refresh(res.data?.id);
      }
      setNotice("Blog categories page saved successfully.");
    } catch (error) {
      const d = error?.response?.data;
      setNotice(
        d?.message || d?.detail || (d ? Object.values(d).flat().join(" ") : "Could not save the page."),
      );
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (fieldName, file) => {
    if (!record) {
      setNotice("Save the page once before uploading images.");
      return;
    }
    setUploadingImages((s) => ({ ...s, [fieldName]: true }));
    try {
      const body = new FormData();
      body.append(fieldName, file);
      await axiosInstance.put(`${API}${record.id}/`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh(record.id);
      setNotice("Image uploaded successfully.");
    } catch (error) {
      setNotice(error?.response?.data?.message || error?.response?.data?.detail || "Could not upload the image.");
    } finally {
      setUploadingImages((s) => ({ ...s, [fieldName]: false }));
    }
  };

  const imageProps = (fieldName) => ({
    fieldName,
    value: content[fieldName],
    disabled: !record,
    uploading: Boolean(uploadingImages[fieldName]),
    onUpload: uploadImage,
    onPreview: setOverlayUrl,
    error: imageErrors[fieldName],
    setError: (msg) =>
      setImageErrors((prev) => {
        const next = { ...prev };
        if (msg) next[fieldName] = msg;
        else delete next[fieldName];
        return next;
      }),
  });


  const textProps = (group, key) => ({
    value: content[group][key],
    rule: LIMITS[`${group}.${key}`],
    error: errors[`${group}.${key}`],
    // placeholder: group === "seo" && FALLBACK_KEYS.includes(key) ? resolvedSeo[key] : undefined,
    onChange: (v) => setGroup(group, key, v),
  });

  const isSuccess = notice.toLowerCase().includes("success");

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8 font-sans">
      {!loaded ? (
        <div className="py-16 text-center text-sm font-semibold text-slate-500">Loading page content...</div>
      ) : (
        <>
          {/* header */}
          <div className="mb-7 flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-6 py-5">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Blog Categories Page</h1>
              <p className="mt-1 text-sm text-slate-500">
                Manage the hero, SEO and structured data of the blog categories listing page.
              </p>
            </div>
          </div>

          {/* floating save */}
          <div className="fixed right-12 top-[88px] z-[1200] flex flex-col items-end gap-1.5">
            <button
              type="button"
              onClick={save}
              disabled={saving || hasBlockingErrors}
              title={hasBlockingErrors ? `${errorCount} field(s) need fixing before you can save` : undefined}
              className={`rounded-lg bg-blue-600 px-[18px] py-[9px] text-[13px] font-semibold text-white shadow-lg shadow-blue-600/30 transition hover:bg-blue-700 ${hasBlockingErrors || saving ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                }`}
            >
              {saving ? "Saving..." : "Save Data"}
            </button>
            {hasBlockingErrors && (
              <span className="rounded-md border border-red-200 bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-600">
                {errorCount} field(s) need fixing
              </span>
            )}
          </div>

          {notice && (
            <div
              className={`mb-5 rounded-lg border px-4 py-3 text-sm font-semibold ${isSuccess
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-red-200 bg-red-50 text-red-700"
                }`}
            >
              {notice}
            </div>
          )}

          {/* SEO */}
          <Section title="SEO">
            <SubBlock title="">
              <Field label="Meta Title" {...textProps("seo", "meta_title")} />
              <Field label="Meta Description" textarea {...textProps("seo", "meta_description")} />
              <Field
                label="Meta Keywords"
                help="Comma separated, e.g. procurement, rfq, suppliers"
                {...textProps("seo", "meta_keywords")}
              />
              <Field
                label="Canonical URL"
                placeholder={CANONICAL_DEFAULT}
                value={content.seo.canonical_url}
                onChange={(v) => setGroup("seo", "canonical_url", v)}
              />
              <Field label="OG Title" {...textProps("seo", "og_title")} />
              <Field label="OG Description" textarea {...textProps("seo", "og_description")} />
              <Field label="Twitter Title" {...textProps("seo", "twitter_title")} />
              <Field label="Twitter Description" textarea {...textProps("seo", "twitter_description")} />

              <label className="mb-4 block text-[13px] font-semibold text-slate-800">
                Robots Index
                <select
                  className={inputCls(false)}
                  value={content.seo.robots_index === false ? "noindex" : "index"}
                  onChange={(e) => setGroup("seo", "robots_index", e.target.value === "index")}
                >
                  <option value="index">index, follow</option>
                  <option value="noindex">noindex, nofollow</option>
                </select>
              </label>
              <label className="mb-4 block text-[13px] font-semibold text-slate-800">
                SEO Content
                <Editor
                  value={content.seo.seo_content || ""}
                  onChange={(html) => setGroup("seo", "seo_content", html)}
                  placeholder="Write SEO content..."
                  minHeight={200}
                />
              </label>
            </SubBlock>

            <SubBlock
              title="Images"
              description="Uploads immediately as soon as a file is chosen. Until you upload your own, OG falls back to the hero image and Twitter falls back to the OG image."
            >
              <ImageUploadField
                label="OG Image"
                {...imageProps("og_image")}
                defaultValue={content.hero_background_image}
                defaultNote="Using the hero background image by default"
              />
              <ImageUploadField
                label="Twitter Image"
                {...imageProps("twitter_image")}
                defaultValue={content.og_image || content.hero_background_image}
                defaultNote="Using the OG image by default"
              />
            </SubBlock>
          </Section>

          {/* SCHEMA */}
          <Section title="Schema" description="Structured data (JSON-LD) stored on the blog categories page record.">
            <SubBlock title="Custom JSON-LD">
              <Field
                label="Custom Schema JSON"
                textarea
                rows={8}
                value={content.schema.custom_json}
                error={errors["schema.custom_json"]}
                help="Optional. Paste a valid JSON-LD object."
                onChange={(v) => setGroup("schema", "custom_json", v)}
              />
            </SubBlock>
            <AdditionalSchemaGenerator schema={content.schema} onChange={(schema) => setField("schema", schema)} />
          </Section>

          {/* HERO */}
          <Section title="Hero Section">
            <SubBlock title="Content">
              <Field label="Heading" {...textProps("hero", "heading")} />
              <Field label="Subheading" textarea {...textProps("hero", "subheading")} />
            </SubBlock>
            <SubBlock title="Images" description="Uploads immediately as soon as a file is chosen.">
              <ImageUploadField label="Hero Background Image" {...imageProps("hero_background_image")} />
            </SubBlock>
          </Section>



          {/* image preview overlay */}
          {overlayUrl && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Image preview"
              onClick={() => setOverlayUrl("")}
              className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/80 p-6"
            >
              <img
                src={overlayUrl}
                alt="Full preview"
                onClick={(e) => e.stopPropagation()}
                className="max-h-full max-w-full rounded-lg bg-white object-contain"
              />
              <button
                type="button"
                onClick={() => setOverlayUrl("")}
                className="fixed right-[18px] top-[18px] rounded-lg border border-white bg-slate-900/60 px-3 py-2 text-lg text-white"
              >
                ×
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}